const post = {
  slug: 'what-cutting-our-aws-bill-actually-took',
  number: 7,
  title: 'What Cutting Our AWS Bill in Half Actually Took',
  date: '2026-06-21',
  readTime: '8 min read',
  views: 81,
  category: 'aws',
  tags: ['AWS', 'FinOps', 'Cloud'],
  excerpt:
    'The AWS bill was climbing every month and nobody could explain why. Bringing it back down was less about clever tricks and more about finally paying attention. Here is what actually worked.',
  cover: null,
  content: `
The AWS bill arrived every month, went up, and got forwarded around with a vague sense of unease and no real plan. Nobody could explain the increase, because nobody could see inside it. It was one large number, and a large number with no breakdown is impossible to argue with. That is the position a lot of teams are quietly in, and it is exactly where we were before we decided to actually look.

Cutting it roughly in half was not a clever trick. It was the slow, slightly boring work of attribution, cleanup, and a couple of structural changes, none of which made anything slower. Here is what actually moved the number.

## You cannot cut what you cannot attribute

The first problem was not technical at all. We could see the total, but we could not see which team, service, or feature was responsible for it. Every conversation about cost ended in a shrug, because nobody could be assigned anything.

So we tagged everything. Every resource got an owner, an environment, and a service tag, enforced so new resources could not be created without them. Once the cost reports could be sliced by those tags, the truth showed up fast, and it was uncomfortable: a handful of services accounted for most of the spend, and several expensive things belonged to projects that had been cancelled months earlier.

\`\`\`text
Before: one number, no owners, no plan
After:  spend by service, by team, by environment -> obvious targets
\`\`\`

Attribution is the unglamorous prerequisite for everything else. Until you have it, you are guessing, and guessing about money tends to be expensive.

## The biggest savings were things nobody was using

Once we could see the breakdown, the largest wins were almost embarrassing. None of them required deep optimization. They required looking.

- Oversized instances provisioned for a peak that never came, running at single-digit CPU around the clock.
- Old environments someone spun up for a test and never tore down.
- Storage volumes and snapshots left behind by servers that no longer existed.
- Data quietly moving between availability zones, racking up transfer charges nobody had noticed.

We right-sized the instances to match real usage, deleted the orphans, and put lifecycle rules on storage so old data aged out automatically instead of accumulating forever. Most of the first wave of savings was simply switching off things we were paying for and not using.

## Commit to the baseline, stay flexible on the rest

After the cleanup, a clear pattern remained. A predictable baseline of compute ran every hour of every day, and a variable amount rode on top of it during busy periods.

On-demand pricing is the expensive way to pay for that steady baseline, because you are paying a premium for flexibility you are not using on the part of the load that never changes. For the always-on floor, committing in advance through savings plans or reserved capacity cut the rate meaningfully. We kept the steady part on a commitment and let the variable part flex on demand, which is exactly the shape the pricing model rewards.

The mistake to avoid is over-committing. You commit to the load you are confident will always be there, not to your most optimistic growth projection, because a commitment you do not use is just a more expensive way to waste money.

## Make cost visible, then it stays down

The cleanup was satisfying, and cleanups do not last on their own. Without a habit, the bill drifts right back up, because new oversized instances and forgotten environments appear at the same rate they always did.

What kept it down was visibility. A simple dashboard of spend by service. A budget alert that fired when something jumped. Cost showing up as a normal topic in engineering reviews rather than a quarterly surprise from finance. When an engineer can see that their change doubled a service's cost the next day, they fix it themselves, without anyone having to chase them.

## A FinOps checklist

The practice that keeps the bill honest:

- Enforce tags for owner, environment, and service on every resource.
- Review spend by tag regularly, and treat unattributed spend as a bug.
- Right-size against real usage, and delete anything nothing is using.
- Put lifecycle rules on storage so old data ages out automatically.
- Commit to the steady baseline, leave the variable part on demand.
- Wire up budget alerts and put cost in front of engineers, not just finance.

## It is an engineering problem, not a finance one

The lasting lesson was that cloud cost is an engineering concern. Finance can read the bill, but only the people who build and run the systems can change what it says. The savings did not come from a negotiation or a spreadsheet. They came from tagging, from deleting what we did not use, from matching capacity to real demand, and from making the number impossible to ignore. Half the bill, and nothing got slower, because we were finally paying for what we actually used.
`,
};

export default post;
